import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Building2, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid or expired reset link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
      <form onSubmit={submit} className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#0F2942] flex items-center justify-center text-white"><Building2 className="h-5 w-5" /></div>
          <span className="font-display text-xl font-bold tracking-tight text-slate-900">EstatePulse</span>
        </div>
        <div className="space-y-2">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Set a new password</h2>
          <p className="text-sm text-slate-500">Choose a new password for your account.</p>
        </div>
        {!token && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">Missing reset token. Please use the link from your email.</div>}
        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="reset-error">{error}</div>}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">New Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" data-testid="reset-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">Confirm Password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="h-11" data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" disabled={submitting || !token} className="w-full h-11 bg-[#0F2942] gap-2" data-testid="reset-submit-button">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Update password
        </Button>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
      </form>
    </div>
  );
}
